import { ArrowLeft } from 'lucide-react';
import { STEPS } from './steps';
import LogoutButton from '../../Components/UI/LogoutButton';
import signupLogo from '../../assets/claraview-v2/signup-logo.webp';
import welcomeImage from '../../assets/claraview-v2/onboarding-welcome.webp';
import practiceImage from '../../assets/claraview-v2/onboarding-practice.webp';
import shareImage from '../../assets/claraview-v2/onboarding-share.webp';

const STEP_LABELS = {
    welcome: 'Welcome',
    practise: 'Practice',
    share: 'Share',
    done: 'Done',
};

/*
 * Per-step background config ported from the Figma source's STEP_CONFIGS. These are literal
 * hex/rgba values (not derived from cv2 tokens): cv2-foreground/cv2-background are pure
 * grayscale, and an earlier attempt to approximate a similar green-tinted glow on the Pricing
 * page from those tokens visibly didn't match the design (twice) — so per-step photo scrims and
 * radial glows are ported verbatim here instead of a third guess. Text/border treatments in the
 * top bar below still use the proven cv2-foreground + color-mix() approach.
 */
const STEP_BACKGROUNDS = {
    welcome: {
        background: '#080c09',
        photoSrc: welcomeImage,
        photoOpacity: 1,
        /*
         * The photo is a wide 1.9:1 frame with the tablet and person sitting centre-right and an
         * empty gradient at the left edge. Default object-center crops symmetrically, so on any
         * viewport narrower than 1.9:1 the subject drifts off the right edge and the dead left
         * margin stays in frame. Biasing right keeps the composition Figma shows; on phones the
         * crop is severe enough that 48% is needed to hold the tablet rather than a dark shoulder.
         */
        photoPosition: 'object-[48%_center] md:object-[62%_center]',
        overlay: 'linear-gradient(0deg, rgba(8,12,9,0.65) 0%, rgba(3,5,4,0.486) 25%, rgba(1,2,1,0.325) 50%, rgba(0,0,0,0.16) 75%, rgba(0,0,0,0) 100%), linear-gradient(90deg, rgba(8,12,9,0.2) 0%, rgba(8,12,9,0.85) 100%)',
    },
    practise: {
        background: '#080c09',
        photoSrc: practiceImage,
        photoOpacity: 1,
        overlay: 'rgba(0,0,0,0.5)',
    },
    share: {
        background: 'radial-gradient(ellipse at 20% 5%, #1c2e24 0%, #121d17 27.5%, #080c09 55%)',
        photoSrc: shareImage,
        photoOpacity: 0.2,
        overlay: null,
    },
    done: {
        background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.3) 100%), radial-gradient(ellipse at 20% 5%, #1c2e24 0%, #121d17 27.5%, #080c09 55%)',
        photoSrc: null,
        photoOpacity: 0,
        overlay: null,
    },
};

const SCOPED_STYLES = `
  .cv2-onboard-progress-past { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); }
  .cv2-onboard-progress-future { color: color-mix(in srgb, var(--cv2-foreground) 13%, transparent); }
  /* Icon-only, so it carries more default contrast than the text Skip link — an unlabelled
   * control has to be findable on its own. The hover disc is the affordance the label used
   * to provide. */
  .cv2-onboard-back {
    color: color-mix(in srgb, var(--cv2-foreground) 38%, transparent);
    transition: color 0.15s ease, background-color 0.15s ease;
  }
  .cv2-onboard-back:hover {
    color: var(--cv2-foreground);
    background-color: color-mix(in srgb, var(--cv2-foreground) 9%, transparent);
  }
  .cv2-onboard-divider { background-color: color-mix(in srgb, var(--cv2-foreground) 12%, transparent); }
`;

const OnboardingLayout = ({ stepKey, children, onBack, showBack = true }) => {
    const bg = STEP_BACKGROUNDS[stepKey];
    const currentIndex = STEPS.findIndex((step) => step.key === stepKey);

    return (
        <div className="cv2-dark relative min-h-dvh flex flex-col overflow-hidden font-aeonik" style={{ background: bg.background }}>
            <style>{SCOPED_STYLES}</style>

            {bg.photoSrc && (
                <img
                    src={bg.photoSrc}
                    alt=""
                    className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${bg.photoPosition || 'object-center'}`}
                    style={{ opacity: bg.photoOpacity }}
                />
            )}
            {bg.overlay && (
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={bg.overlay.includes('gradient') ? { backgroundImage: bg.overlay } : { backgroundColor: bg.overlay }}
                />
            )}

            <div className="relative z-10 flex items-center justify-between gap-4 px-7 sm:px-12 pt-6 pb-6">
                {/* Back leads the row — the leading edge is where a back affordance is looked for,
                 * and the icon reads as "back" without a label. The rule separates it from the
                 * wordmark so the two don't merge into one lockup. */}
                <div className="flex items-center gap-3 sm:gap-4">
                    {showBack && onBack && (
                        <>
                            <button
                                type="button"
                                onClick={onBack}
                                aria-label="Go back"
                                title="Back"
                                className="cv2-onboard-back -ml-2.5 h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-full"
                            >
                                <ArrowLeft className="w-[17px] h-[17px]" strokeWidth={1.5} />
                            </button>
                            {/* Dropped on phones — the two gaps it needs cost more width than
                              * the separation is worth where space is actually tight. */}
                            <span className="cv2-onboard-divider hidden sm:block h-4 w-px shrink-0" aria-hidden="true" />
                        </>
                    )}
                    <img src={signupLogo} alt="Claraview" className="h-[18px] w-auto opacity-75 object-contain shrink-0" />
                </div>

                <div className="hidden sm:flex items-center gap-5">
                    {STEPS.map((step, i) => {
                        const isCurrent = i === currentIndex;
                        const isPast = i < currentIndex;
                        return (
                            <div key={step.key} className="flex items-center gap-1.5">
                                {isCurrent && <div className="w-1 h-1 rounded-full bg-cv2-foreground shrink-0" />}
                                <span
                                    className={`font-spaceMono text-[9px] tracking-[0.8px] uppercase transition-all duration-300 ${
                                        isCurrent ? 'text-cv2-foreground' : isPast ? 'cv2-onboard-progress-past' : 'cv2-onboard-progress-future'
                                    }`}
                                >
                                    {STEP_LABELS[step.key]}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center gap-5 sm:gap-6">
                    <LogoutButton />
                </div>
            </div>

            <div className="relative z-10 flex flex-col flex-1">
                {children}
            </div>
        </div>
    );
};

export default OnboardingLayout;
